/**
 * Author: Robert Banziziki
 * Date: 30/12/2020
 */
import { ApiEntity } from "../../Api/api.model";
import { API_ENDPOINTS } from "../../Api/api";
import { validateStruct } from "../../../utils/validate";
import { string, type, optional, array, size, number } from "superstruct";
import { MediaAsset, MediaAssetStruct } from "../../../utils/assets";

interface OptionPickerItem {
  name: string;
  placeholder: string;
  optionList: Array<{ option: string }>;
}

interface RangePickerItem {
  name: string;
  placeholder: string;
  min: number;
  max: number;
  unit: string;
  step:number;
}
interface DatePickerItem {
  name: string;
  placeholder: string;
  startDate: number;
  endDate: number;
}

interface SelectQuestion {
  id: string;
  name: string;
  title:string;
  coverImg: MediaAsset;
  optionPicker: Array<OptionPickerItem>;
  rangePicker: Array<RangePickerItem>;
  datePicker: Array<DatePickerItem>;
}

interface QcmQuestion {
  id: string;
  name: string;
  coverImg: MediaAsset;
  title: string;
  choices: Array<{ text: string }>;
}

interface Quizz {
  id: string;
  name: string;
  coverImg: MediaAsset;
  completionTarget: { target: string; param?: string };
  qcm_questions: Array<QcmQuestion>;
  select_questions: Array<SelectQuestion>;
  title: string;
  description: string;
  completionTitle:string;
  completionText:string;
  completionImg:MediaAsset;
}

function formatQcmQuestions(rawData: Array<Record<string, unknown>>) {
  return rawData.map((raw) => ({
    ...raw,
    id: (raw["id"] as number).toString(),
  }));
}

function formatSelectQuestions(rawData: Array<Record<string, unknown>>) {
  return rawData.map((raw) => ({
    ...raw,
    id: (raw?.id as number).toString(),
    datePicker: raw?.datePicker
      ? (raw.datePicker as Array<Record<string, unknown>>).map(
          (datePickerItem) => ({
            ...datePickerItem,
            startDate: datePickerItem?.startDate
              ? new Date(datePickerItem.startDate as string).getTime()
              : undefined,
            endDate: datePickerItem?.endDate
              ? new Date(datePickerItem.endDate as string).getTime()
              : undefined,
          })
        )
      : [],
  }));
}

const QcmQuestionStruct = size(
  array(
    type({
      id: string(),
      name: string(),
      coverImg: MediaAssetStruct,
      title: string(),
      choices: array(type({ text: string() })),
    })
  ),
  1,
  Infinity
);

const OptionPickerStruct = size(
  array(
    type({
      name: string(),
      placeholder: string(),
      optionList: size(
        array(
          type({
            option: string(),
          })
        ),
        1,
        10
      ),
    })
  ),
  0,
  Infinity
);

const RangePickerStuct = size(
  array(
    type({
      name: string(),
      placeholder: string(),
      min: number(),
      max: number(),
      unit: string(),
      step:number()
    })
  ),
  0,
  Infinity
);

const DatePickerStuct = size(
  array(
    type({
      name: string(),
      placeholder: string(),
      startDate: number(),
      endDate: number(),
    })
  ),
  0,
  Infinity
);

const SelectQuestionStruct = size(
  array(
    type({
      id: string(),
      coverImg: MediaAssetStruct,
      name: string(),
      title:string(),
      optionPicker: OptionPickerStruct,
      rangePicker: RangePickerStuct,
      datePicker: DatePickerStuct,
    })
  ),
  0,
  Infinity
);

const QuzzStruct = type({
  id: string(),
  name: string(),
  coverImg: MediaAssetStruct,
  completionTarget: type({ target: string(), param: optional(string()) }),
  title: string(),
  description: string(),
  qcm_questions: QcmQuestionStruct,
  select_questions: SelectQuestionStruct,
  completionTitle:string(),
  completionText:string(),
  completionImg:MediaAssetStruct
});
class QuizzEntity extends ApiEntity<Array<Quizz>> {
  readonly entity: API_ENDPOINTS = "quizzes";
  constructor() {
    super("quizzes");
  }
  validate(rawData: Array<any>): Array<Quizz> {
    return rawData.map((rawItem) =>
      validateStruct<Quizz>(rawItem, QuzzStruct, this.endPoint, [
        { field: "id", transform: (id: number) => id.toString() },
        { field: "qcm_questions", transform: formatQcmQuestions },
        { field: "select_questions", transform: formatSelectQuestions },
      ])
    );
  }
}

export { Quizz, QuizzEntity, QcmQuestion,SelectQuestion };
